import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import AdminService from '../../services/adminService';
import ScoringEngine from '../../services/scoringEngine';
import { supabase } from '../../services/supabaseClient';

/**
 * Multilingual System Test Page
 *
 * Comprehensive testing interface for:
 * - Language switching functionality
 * - Translation loading and display
 * - Database connectivity
 * - Scoring engine calculations
 * - Country and pathway management
 */
const MultilingualTestPage = () => {
  const { t, currentLanguage, availableLanguages, changeLanguage, isLoading, getCountryName } = useLanguage();
  const [testResults, setTestResults] = useState([]);
  const [isRunningTests, setIsRunningTests] = useState(false);
  const [testData, setTestData] = useState({
    countries: [],
    pathways: [],
    translations: []
  });

  // Sample user profile for scoring tests
  const sampleUserProfile = {
    personalInfo: {
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      phone: '+1234567890',
      dateOfBirth: '1990-01-01',
      nationality: 'USA',
      currentCountry: 'USA'
    },
    education: {
      highestLevel: 'bachelors',
      field: 'Computer Science'
    },
    experience: [
      {
        company: 'Tech Corp',
        position: 'Software Engineer',
        duration: '3 years'
      }
    ],
    languageSkills: {
      english: 'fluent',
      german: 'intermediate'
    },
    financialInfo: {
      fundsAvailable: 15000,
      monthlyIncome: 5000
    }
  };

  const addTestResult = (testName, status, message, details = null) => {
    setTestResults(prev => [...prev, {
      testName,
      status, // 'success', 'error', 'warning', 'info'
      message,
      details,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  const runAllTests = async () => {
    setIsRunningTests(true);
    clearResults();

    try {
      // Test 1: Database Connection
      addTestResult('Database Connection', 'info', 'Testing Supabase connection...');
      const { data: connectionTest, error: connectionError } = await supabase
        .from('countries')
        .select('count')
        .limit(1);

      if (connectionError) {
        addTestResult('Database Connection', 'error', 'Failed to connect to database', connectionError);
      } else {
        addTestResult('Database Connection', 'success', 'Successfully connected to database');
      }

      // Test 2: Language Data Loading
      addTestResult('Language Data', 'info', 'Loading language data...');
      const languages = await AdminService.getAdminStats();
      addTestResult('Language Data', 'success', `Loaded data for ${languages.translations} translations`);

      // Test 3: Countries Loading
      addTestResult('Countries Loading', 'info', 'Loading countries data...');
      const countries = await AdminService.getCountries();
      setTestData(prev => ({ ...prev, countries }));
      addTestResult('Countries Loading', 'success', `Loaded ${countries.length} countries`);

      // Test 4: Translation System
      addTestResult('Translation System', 'info', 'Testing translation functionality...');
      const translationTests = [
        { key: 'wizard.title', expected: 'Lead Qualification Wizard' },
        { key: 'common.next', expected: 'Next' },
        { key: 'wizard.select_country', expected: 'Select your destination country' }
      ];

      let translationErrors = 0;
      for (const test of translationTests) {
        const translation = t(test.key, '');
        if (!translation || translation === test.key) {
          addTestResult('Translation System', 'warning', `Missing translation for key: ${test.key}`);
          translationErrors++;
        }
      }

      if (translationErrors === 0) {
        addTestResult('Translation System', 'success', 'All translations loaded correctly');
      }

      // Test 5: Language Switching
      addTestResult('Language Switching', 'info', 'Testing language switching...');
      for (const language of availableLanguages) {
        try {
          await changeLanguage(language.code);
          addTestResult('Language Switching', 'success', `Switched to ${language.native_name}`);
        } catch (error) {
          addTestResult('Language Switching', 'error', `Failed to switch to ${language.native_name}`, error);
        }
      }

      // Test 6: Scoring Engine
      addTestResult('Scoring Engine', 'info', 'Testing scoring engine...');
      try {
        const scoringResults = await ScoringEngine.calculateAllPathwayScores(
          sampleUserProfile,
          null,
          'DEU'
        );

        if (scoringResults && scoringResults.pathways) {
          const pathwayCount = Object.keys(scoringResults.pathways).length;
          addTestResult('Scoring Engine', 'success', `Calculated scores for ${pathwayCount} pathways`);

          // Log pathway results
          for (const [pathwayKey, result] of Object.entries(scoringResults.pathways)) {
            addTestResult('Scoring Engine', 'info',
              `${pathwayKey}: ${result.percentageScore}% (Eligible: ${result.isEligible})`);
          }
        } else {
          addTestResult('Scoring Engine', 'warning', 'Scoring engine returned no results');
        }
      } catch (error) {
        addTestResult('Scoring Engine', 'error', 'Scoring engine failed', error);
      }

      // Test 7: Country Name Translations
      addTestResult('Country Translations', 'info', 'Testing country name translations...');
      try {
        const germanyNameEn = await getCountryName('DEU');
        addTestResult('Country Translations', 'success', `Germany name (${currentLanguage}): ${germanyNameEn}`);
      } catch (error) {
        addTestResult('Country Translations', 'warning', 'Could not test country translations', error);
      }

      // Test 8: Admin Service Functions
      addTestResult('Admin Services', 'info', 'Testing admin service functions...');
      try {
        const stats = await AdminService.getAdminStats();
        addTestResult('Admin Services', 'success',
          `Admin stats: ${stats.countries} countries, ${stats.pathways} pathways`);
      } catch (error) {
        addTestResult('Admin Services', 'error', 'Admin services test failed', error);
      }

      addTestResult('Test Suite', 'success', `Completed ${testResults.length} tests`);

    } catch (error) {
      addTestResult('Test Suite', 'error', 'Test suite failed with unexpected error', error);
    } finally {
      setIsRunningTests(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '❓';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      case 'info':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Multilingual System Test Page
          </h1>
          <p className="text-gray-600">
            Comprehensive testing for language support, database connectivity, and scoring engine
          </p>
        </div>

        {/* Language Controls */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Language Controls</h2>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">Current Language:</span>
            <select
              value={currentLanguage}
              onChange={(e) => changeLanguage(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md"
            >
              {availableLanguages.map(lang => (
                <option key={lang.code} value={lang.code}>
                  {lang.native_name} ({lang.code})
                </option>
              ))}
            </select>
            {isLoading && <span className="text-sm text-blue-600">Loading translations...</span>}
          </div>
        </div>

        {/* Test Controls */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Test Controls</h2>
            <div className="space-x-3">
              <button
                onClick={clearResults}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear Results
              </button>
              <button
                onClick={runAllTests}
                disabled={isRunningTests}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isRunningTests ? 'Running Tests...' : 'Run All Tests'}
              </button>
            </div>
          </div>
        </div>

        {/* Test Results */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">
            Test Results ({testResults.length} tests)
          </h2>

          {testResults.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              No tests run yet. Click "Run All Tests" to start testing.
            </p>
          ) : (
            <div className="space-y-3">
              {testResults.map((result, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${getStatusColor(result.status)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <span className="text-lg">{getStatusIcon(result.status)}</span>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{result.testName}</span>
                          <span className="text-sm text-gray-500">{result.timestamp}</span>
                        </div>
                        <p className="mt-1">{result.message}</p>
                        {result.details && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-sm font-medium">
                              Details
                            </summary>
                            <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
                              {JSON.stringify(result.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sample Translations */}
        <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
          <h2 className="text-lg font-semibold mb-4">Sample Translations ({currentLanguage})</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Wizard Title:</span> {t('wizard.title')}
            </div>
            <div>
              <span className="font-medium">Next Button:</span> {t('common.next')}
            </div>
            <div>
              <span className="font-medium">Previous Button:</span> {t('common.previous')}
            </div>
            <div>
              <span className="font-medium">Submit Button:</span> {t('common.submit')}
            </div>
            <div>
              <span className="font-medium">Loading:</span> {t('common.loading')}
            </div>
            <div>
              <span className="font-medium">Required:</span> {t('common.required')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultilingualTestPage;